// @ts-strict-ignore
import { ProjectInfoPropertyDef } from '@project/components/project/constants/project-info-property-def';
import { Dictionary } from 'typescript-collections';
import { ArticleCompositionVO, ArticleVO } from '@article/model/vo/article.vo';
import { RepresentationVO } from '@shared/vo/representation.vo';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { PropertyVO, StringValueInLocalesVO } from '../../components/article/model/vo/property.vo';
import { JsonObject, JsonProperty } from '@shared/convert/convert-core';
import { LabelDuplicateVO } from '@project/model/vo/label-duplicate.vo';
import { StringUtils } from '@shared/util/string-utils';
import { omit } from 'lodash';
import { ArticleNumberLong } from '../article-number-long';
import { ProjectType } from '../project-type';
import { Properties } from '@article/util/properties';
import { MdmGroupVO } from '@user/model/vo/user.vo';

@JsonObject('MetaElementInfoVO')
export class MetaElementInfoVO {
  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('originalName', 'String')
  public originalName: string = undefined;
}

@JsonObject('ArticleAttributeVO')
export class ArticleAttributeVO {
  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('values', '[String]')
  public values: string[] = undefined;
}

@JsonObject('ArticleInfoVO')
export class ArticleInfoVO {
  @JsonProperty('guid', 'String')
  public guid: string = undefined;

  @JsonProperty('identity', 'Number')
  public identity: number = undefined;

  @JsonProperty('version', 'Number')
  public version: number = undefined;

  @JsonProperty('attributes', '[ArticleAttributeVO]')
  public attributes: ArticleAttributeVO[] = undefined;

  @JsonProperty('metaElements', '[MetaElementInfoVO]')
  public metaElements: MetaElementInfoVO[] = undefined;
}

@JsonObject('ProjectOptionLocalizationVO')
export class ProjectOptionLocalizationVO {
  @JsonProperty('id', 'Number')
  public id: number = undefined;

  @JsonProperty('locale', 'String')
  public locale: string = undefined;

  @JsonProperty('value', 'String')
  public value: string = undefined;
}

@JsonObject('ProjectOptionVO')
export class ProjectOptionVO {
  @JsonProperty('id', 'Number')
  public id: number = undefined;

  @JsonProperty('identifier', 'String')
  public identifier: string = undefined;

  @JsonProperty('order', 'Number')
  public order: number = undefined;

  @JsonProperty('value', 'String')
  public value: string = undefined;

  @JsonProperty('localizations', '[ProjectOptionLocalizationVO]')
  public localizations: ProjectOptionLocalizationVO[] = undefined;
}

@JsonObject('ProjectOptionDefLocalizationVO')
export class ProjectOptionDefLocalizationVO {
  @JsonProperty('id', 'Number')
  public id: number = undefined;

  @JsonProperty('value', 'String')
  public value: string = undefined;

  @JsonProperty('locale', 'String')
  public locale: string = undefined;
}

@JsonObject('ProjectOptionDefVO')
export class ProjectOptionDefVO {
  @JsonProperty('id', 'Number')
  public id: number = undefined;

  @JsonProperty('identifier', 'String')
  public identifier: string = undefined;

  @JsonProperty('isDefault', 'Boolean')
  public isDefault: boolean = undefined;

  @JsonProperty('isVisible', 'Boolean')
  public isVisible: boolean = undefined;

  @JsonProperty('isRequired', 'Boolean')
  public isRequired: boolean = undefined;

  @JsonProperty('isMultiLine', 'Boolean')
  public isMultiLine: boolean = undefined;

  @JsonProperty('order', 'Number')
  public order: number = undefined;

  @JsonProperty('defaultValue', 'String')
  public defaultValue: string = undefined;

  @JsonProperty('localizations', '[ProjectOptionDefLocalizationVO]')
  public localizations: ProjectOptionDefLocalizationVO[] = undefined;

  @JsonProperty('maxLength', 'Number')
  public maxLength: number = undefined;
}

@JsonObject('CarrierBlockVO')
export class CarrierBlockVO {
  @JsonProperty('guid', 'String')
  public guid: string = undefined;

  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('structure', 'String')
  public structure: string = undefined;

  @JsonProperty('space', 'Number')
  public space: number = undefined;

  public articleGuids: string[] = [];
  public guidsSorted: boolean;

  public isEmpty(): boolean {
    return !this.articleGuids || this.articleGuids.length === 0;
  }

  public containsArticleGuid(guid: string): boolean {
    if (!guid || this.isEmpty()) {
      return false;
    }
    return this.articleGuids.indexOf(guid) >= 0;
  }

  // override default object method toJSON() (which is invoked when the object should be serialize)
  // to exclude transient properties and send correct object to server
  public toJSON() {
    return {
      guid: this.guid,
      name: this.name,
      space: this.space,
      structure: this.structure,
    };
  }
}

@JsonObject('UserInfoVO')
export class UserInfoVO {

  constructor(userId?: number, username?: string, isDeleted?: boolean) {
    this.userId = userId;
    this.username = username;
    this.isDeleted = isDeleted ?? false;
  }

  @JsonProperty('userId', 'Number')
  public readonly userId: number = undefined;

  @JsonProperty('userName', 'String')
  public readonly username: string = undefined;

  @JsonProperty('isDeleted', 'Boolean')
  public readonly isDeleted: boolean = false;
}

@JsonObject('ProjectHistoryEntryVO')
export class ProjectHistoryEntryVO {
  @JsonProperty('projectId', 'Number')
  public projectId: number = undefined;

  @JsonProperty('projectVersion', 'Number')
  public projectVersion: number = undefined;

  @JsonProperty('projectVersionDisplayString', 'String')
  public projectVersionDisplayString: string = undefined;

  @JsonProperty('projectVersionSaveDate', 'DateConverter', true)
  public projectVersionSaveDate: Date = undefined;

  @JsonProperty('creationDateServerTimeStamp', 'String')
  public creationDateServerTimeStamp: string = undefined;

  @JsonProperty('documentVersion', 'Number')
  public documentVersion: number = undefined;

  @JsonProperty('modifiedBy', 'UserInfoVO')
  public modifiedBy: UserInfoVO = undefined;

  @JsonProperty('action', 'Number')
  public action: number = undefined; // see: ProjectHistoryActionDef

  @JsonProperty('date', 'DateConverter', true)
  public date: Date = undefined;

  @JsonProperty('finalizationState', 'Number')
  public finalizationState: number = undefined;

  @JsonProperty('receiverEmail', 'String')
  public receiverEmail: string = undefined;

  @JsonProperty('priceCalculationRefId', 'String')
  public priceCalculationRefId: string = undefined;
}

@JsonObject('ProjectHistoryVO')
export class ProjectHistoryVO {
  @JsonProperty('projectId', 'Number')
  public projectId: number = undefined;

  @JsonProperty('projectName', 'String')
  public projectName: string = undefined;

  @JsonProperty('projectType', 'String')
  public projectType: ProjectType = undefined;

  @JsonProperty('projectPath', 'String')
  public projectPath: string = undefined;

  @JsonProperty('isProjectLocked', 'Boolean')
  public isProjectLocked: boolean = false;

  @JsonProperty('articleNumberLong', 'String')
  public articleNumberLong: string = undefined;

  @JsonProperty('projectOwner', 'UserInfoVO')
  public projectOwner: UserInfoVO = undefined;

  @JsonProperty('lastCall', 'DateConverter', true)
  public lastCall: Date = undefined;

  @JsonProperty('materialStatus', 'Number')
  public materialStatus: number = undefined;

  @JsonProperty('projectHistoryEntries', '[ProjectHistoryEntryVO]')
  public projectHistoryEntries: ProjectHistoryEntryVO[] = undefined;
}

@JsonObject('ProjectHeadVO')
export class ProjectHeadVO {
  public static readonly TRANSIENT_PROPERTIES: string[] = ['isArticleProject', 'isWSPImportedProject', 'articleNumberExtension', 'typeClassIdTemp'];
  public static readonly MAX_NAME_LENGTH: number = 500;

  @JsonProperty('id', 'Number')
  public id: number = undefined;

  @JsonProperty('guid', 'String')
  public guid: string = undefined;

  @JsonProperty('currentVersion', 'Number')
  public currentVersion: number = undefined;

  // this property exists, so version can be retrieved in cases where project options are not transmitted.
  @JsonProperty('currentVersionDisplayString', 'String')
  public currentVersionDisplayString: string = undefined;

  @JsonProperty('documentVersion', 'Number')
  public documentVersion: number = undefined;

  @JsonProperty('creationDate', 'DateConverter', true)
  public creationDate: Date = undefined;

  @JsonProperty('creationDateServerTimeStamp', 'String')
  public creationDateServerTimeStamp: string = undefined;

  @JsonProperty('modificationDateServerTimeStamp', 'String')
  public modificationDateServerTimeStamp: string = undefined;

  @JsonProperty('modificationDate', 'DateConverter', true)
  public modificationDate: Date = undefined;

  @JsonProperty('exported', 'String')
  public exported: string = undefined;

  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('projectGroup', 'ProjectGroupVO')
  public projectGroup: ProjectGroupVO = undefined;

  @JsonProperty('assortmentId', 'Number')
  public assortmentId: number = undefined;

  @JsonProperty('finalizationState', 'Number')
  public finalizationState?: number | null = undefined;

  @JsonProperty('articleNumber', 'String')
  public articleNumber: string = undefined;

  // [Transient]
  @JsonProperty('articleNumberExtension', 'String')
  public articleNumberExtension: string = undefined;

  @JsonProperty('articleNumberLong', 'ArticleNumberLong')
  public articleNumberLong: ArticleNumberLong = undefined;

  @JsonProperty('originProjectArticleNumberLong', 'ArticleNumberLong')
  public originProjectArticleNumberLong: ArticleNumberLong = undefined;

  @JsonProperty('drawingNumber', 'String')
  public drawingNumber: string = undefined;

  @JsonProperty('typeClassId', 'String')
  public typeClassId: string = undefined;

  // TODO: Remarks taken from flex version: typeClassIdTemp is introduced here temporarily so it can be used to fill in input field inside PDM-Export dialog.
  // typeClassId itself can currently not be used for this purpose, because it is overwritten during PDM-Export with current value from database. Only later in the process entered value from user gets stored there.
  // [Transient]
  public typeClassIdTemp: string = undefined;

  @JsonProperty('projectType', 'String')
  public projectType: ProjectType = undefined;

  @JsonProperty('fNumber', 'String')
  public fNumber: string = undefined;

  @JsonProperty('totalPrice', 'Number')
  public totalPrice: number = undefined;

  @JsonProperty('totalDemand', 'Number')
  public totalDemand: number = undefined;

  @JsonProperty('batchSize', 'Number')
  public batchSize: number = undefined;

  @JsonProperty('projectComment', 'StringValueInLocalesVO')
  public projectComment: StringValueInLocalesVO = undefined;

  @JsonProperty('internalProjectComment', 'String')
  public internalProjectComment: string = undefined;

  @JsonProperty('isLatestVersion', 'Boolean')
  public isLatestVersion: boolean = true;

  // [Transient]
  @JsonProperty('isArticleProject', 'Boolean')
  public isArticleProject: boolean = false;

  // [Transient]
  @JsonProperty('isWSPImportedProject', 'Boolean')
  public isWSPImportedProject: boolean = false;

  @JsonProperty('isSharedReadOnly', 'Boolean')
  public isSharedReadOnly: boolean = undefined;

  @JsonProperty('isArticleNumberGenerated', 'Boolean')
  public isArticleNumberGenerated: boolean = undefined;

  @JsonProperty('isLocked', 'Boolean')
  public isLocked: boolean = false;

  @JsonProperty('priceCalculationRefId', 'String')
  public priceCalculationRefId: string = undefined;

  @JsonProperty('caeType', 'Number')
  public caeType: number = undefined;

  @JsonProperty('lastCall', 'DateConverter', true)
  public lastCall: Date = undefined;

  @JsonProperty('materialStatus', 'Number')
  public materialStatus: number = undefined;

  @JsonProperty('latestProjectHistoryEntry', 'ProjectHistoryEntryVO')
  public latestProjectHistoryEntry: ProjectHistoryEntryVO = undefined;

  @JsonProperty('temporaryLinkGUID', 'String')
  public temporaryLinkGUID: string = undefined;

  @JsonProperty('isPinned', 'Boolean')
  public isPinned: boolean = undefined;

  // override default object method toJSON() (which is invoked when the object should be serialize)
  // to exclude transient properties and send correct object to server
  public toJSON() {
    return omit(this, ProjectVO.TRANSIENT_PROPERTIES);
  }
}

@JsonObject('ProjectVO')
export class ProjectVO extends ProjectHeadVO{
  public static readonly TRANSIENT_PROPERTIES: string[] = ['isArticleProject', 'isWSPImportedProject', 'articleNumberExtension', 'typeClassIdTemp', '_infoProperties'];

  @JsonProperty('compositions', '[ArticleCompositionVO]')
  public compositions: ArticleCompositionVO[] = undefined;

  @JsonProperty('representations', '[RepresentationVO]')
  public representations: RepresentationVO[] = undefined;

  @JsonProperty('dynamicArticlesInfo', '[ArticleInfoVO]')
  public dynamicArticlesInfo: ArticleInfoVO[] = undefined;

  @JsonProperty('options', '[ProjectOptionVO]')
  public options: ProjectOptionVO[] = undefined;

  @JsonProperty('infoProperties', '[PropertyVO]')
  private _infoProperties: PropertyVO[] = [];

  public get infoProperties(): Properties<ProjectInfoPropertyDef> {
    return new Properties(this._infoProperties ?? []);
  }

  public set infoPropertyVOs(infoProperties: PropertyVO[]) {
    this._infoProperties = infoProperties;
  }

  @JsonProperty('carrierBlocks', '[CarrierBlockVO]')
  public carrierBlocks: CarrierBlockVO[] = undefined;

  // override default object method toJSON() (which is invoked when the object should be serialize)
  // to exclude transient properties and send correct object to server
  public toJSON() {
    return {
      ...omit(this, ProjectVO.TRANSIENT_PROPERTIES),
      infoProperties: this._infoProperties,
    };
  }
}

@JsonObject('ComplexProjectVO')
export class ComplexProjectVO {
  @JsonProperty('project', 'ProjectVO')
  public project: ProjectVO = undefined;

  @JsonProperty('articles', 'ArticleVOsConverter', true)
  public articles: ArticleVO[] = undefined;

  @JsonProperty('dynamicArticlesMap', 'StringArticleVODictionaryConverter', true)
  public dynamicArticlesMap: Dictionary<string, ArticleVO> = undefined;

  @JsonProperty('compositeArticleProjectsMap', 'StringProjectVODictionaryConverter', true)
  public compositeArticleProjectsMap: Dictionary<string, ProjectVO> = undefined;

  // [Transient]
  public isArticleProject: boolean = false;

  // [Transient]
  public isWSPImportedProject: boolean = false;

  // [Transient]
  public isDirtyProject: boolean = false;

  // override default object method toJSON() (which is invoked when the object should be serialize)
  // to exclude transient properties and send correct object to server
  public toJSON() {
    return {
      project: this.project,
      articles: this.articles,
      dynamicArticlesMap: this.dynamicArticlesMap,
      compositeArticleProjectsMap: this.compositeArticleProjectsMap,
    };
  }
}

@JsonObject('ProjectSaveInfoVO')
export class ProjectSaveInfoVO {
  @JsonProperty('projectId', 'Number')
  public projectId: number = undefined;

  @JsonProperty('highestFinalizedProjectVersion', 'Number')
  public highestFinalizedProjectVersion: number = undefined;

  @JsonProperty('highestFinalizedProjectVersionDisplayString', 'String')
  public highestFinalizedProjectVersionDisplayString: string = undefined;

  @JsonProperty('highestFinalizedProjectVersionSaveDate', 'DateConverter', true)
  public highestFinalizedProjectVersionSaveDate: Date = undefined;

  @JsonProperty('highestFinalizedProjectVersionArticleNumberLong', 'String')
  public highestFinalizedProjectVersionArticleNumberLong: string = undefined;

  @JsonProperty('projectWithHighestVersion', 'ProjectVO')
  public projectWithHighestVersion: ProjectHeadVO = undefined;

  @JsonProperty('finalizedProjectVersionDisplayStrings', '[String]')
  public finalizedProjectVersionDisplayStrings: string[] = undefined;

  @JsonProperty('existingSubProjectNames', '[String]')
  public existingSubProjectNames: string[] = undefined;

  @JsonProperty('readOnlySubProjectNames', '[String]')
  public readOnlySubProjectNames: string[] = undefined;

  @JsonProperty('requestedProjectVersion', 'Number')
  public requestedProjectVersion: number = undefined;

  @JsonProperty('requestedProjectVersionDisplayString', 'String')
  public requestedProjectVersionDisplayString: string = undefined;

  @JsonProperty('requestedProjectVersionSaveDate', 'DateConverter', true)
  public requestedProjectVersionSaveDate: Date = undefined;

  @JsonProperty('requestedProjectVersionFound', 'Boolean')
  public requestedProjectVersionFound: boolean = undefined;

  @JsonProperty('requestedProjectDocumentVersion', 'Number')
  public requestedProjectDocumentVersion: number = undefined;

  public existsFinalizedProjectVersionDisplayString(projectVersionDisplayString: string): boolean {
    if (!this.finalizedProjectVersionDisplayStrings || this.finalizedProjectVersionDisplayStrings.length === 0) {
      return false;
    }
    if (StringUtils.isEmpty(projectVersionDisplayString)) {
      return false;
    }
    const projectVersionDisplayStr: string = StringUtils.trim(projectVersionDisplayString);
    for (const finalizedProjectVersionDisplayString of this.finalizedProjectVersionDisplayStrings) {
      if (StringUtils.trim(finalizedProjectVersionDisplayString) === projectVersionDisplayStr) {
        return true;
      }
    }
    return false;
  }
}

@JsonObject('ProjectArticleNumberInfoVO')
export class ProjectArticleNumberInfoVO {
  @JsonProperty('articleNumber', 'String')
  public articleNumber: string = undefined;

  @JsonProperty('articleNumberLong', 'String')
  public articleNumberLong: string = undefined;

  @JsonProperty('drawingNumber', 'String')
  public drawingNumber: string = undefined;
}

@JsonObject('RequestFinalizeProjectResultVO')
export class RequestFinalizeProjectResultVO {
  @JsonProperty('projectArticleNumberInfo', 'ProjectArticleNumberInfoVO')
  public projectArticleNumberInfo: ProjectArticleNumberInfoVO = undefined;

  @JsonProperty('resultState', 'String')
  public resultState: string = undefined;

  @JsonProperty('isArticleNumberGenerated', 'Boolean')
  public isArticleNumberGenerated: boolean = false;

  @JsonProperty('duplicateArticleNumbers', '[LabelDuplicateVO]')
  public duplicateArticleNumbers: LabelDuplicateVO[] = undefined;
}

@JsonObject('ProjectFinalizationInfoVO')
export class ProjectFinalizationInfoVO {
  @JsonProperty('articleNumber', 'String')
  public articleNumber: string = undefined;

  @JsonProperty('articleNumberLong', 'String')
  public articleNumberLong: string = undefined;

  @JsonProperty('drawingNumber', 'String')
  public drawingNumber: string = undefined;

  @JsonProperty('typeClassId', 'String')
  public typeClassId: string = undefined;

  @JsonProperty('currentState', 'String')
  public currentState: string = undefined;

  @JsonProperty('duplicateArticleNumbers', '[LabelDuplicateVO]')
  public duplicateArticleNumbers: LabelDuplicateVO[] = undefined;

  @JsonProperty('projectWeight', 'Number')
  public projectWeight: number = undefined;

  @JsonProperty('projectHeight', 'Number')
  public projectHeight: number = undefined;

  @JsonProperty('projectLength', 'Number')
  public projectLength: number = undefined;

  @JsonProperty('projectWidth', 'Number')
  public projectWidth: number = undefined;

  @JsonProperty('fNumber', 'String')
  public fNumber: string = undefined;

  @JsonProperty('mainNote', 'Number')
  public mainNote: number = undefined;

  @JsonProperty('productFamily', 'Number')
  public productFamily: number = undefined;

  @JsonProperty('customsTariffNumber', 'String')
  public customsTariffNumber: string = undefined;

  @JsonProperty('additionalNote01', 'Number')
  public additionalNote01: number = undefined;

  @JsonProperty('additionalNote02', 'Number')
  public additionalNote02: number = undefined;

  @JsonProperty('additionalNote03', 'Number')
  public additionalNote03: number = undefined;

  @JsonProperty('additionalNote04', 'Number')
  public additionalNote04: number = undefined;

  @JsonProperty('additionalNote05', 'Number')
  public additionalNote05: number = undefined;

  @JsonProperty('additionalNote06', 'Number')
  public additionalNote06: number = undefined;

  @JsonProperty('additionalNote07', 'Number')
  public additionalNote07: number = undefined;

  @JsonProperty('additionalNote08', 'Number')
  public additionalNote08: number = undefined;

  @JsonProperty('additionalNote09', 'Number')
  public additionalNote09: number = undefined;

  @JsonProperty('additionalNote10', 'Number')
  public additionalNote10: number = undefined;

  @JsonProperty('additionalNote11', 'Number')
  public additionalNote11: number = undefined;

  @JsonProperty('additionalNote12', 'Number')
  public additionalNote12: number = undefined;

  @JsonProperty('documentVersion', 'Number')
  public documentVersion: number = undefined;

  @JsonProperty('legacyFinalization', 'Boolean')
  public legacyFinalization: boolean = false;

  @JsonProperty('countryCode', 'String')
  public countryCode: string = undefined;

  @JsonProperty('tcVersion', 'Number')
  public tcVersion: number = undefined;

  @JsonProperty('onlyDrawing', 'Boolean')
  public onlyDrawing: boolean = false;

  @JsonProperty('printConverterPdfNames', '[String]')
  public printConverterPdfNames: string[] = [];

  @JsonProperty('mdmGroup', 'MdmGroupVO')
  public mdmGroup: MdmGroupVO = undefined;
}

@JsonObject('CustomsTariffNumberVO')
export class CustomsTariffNumberVO {
  @JsonProperty('value', 'String')
  public value: string = undefined;

  @JsonProperty('sortOrder', 'Number')
  public sortOrder: number = undefined;

  public static create(value: string): CustomsTariffNumberVO {
    const vo: CustomsTariffNumberVO = new CustomsTariffNumberVO();
    vo.value = value;
    return vo;
  }
}

@JsonObject('FinalizationPreselectionVO')
export class FinalizationPreselectionVO {
  @JsonProperty('articleNumber', 'String')
  public articleNumber: string = undefined;

  @JsonProperty('classId', 'String')
  public classId: string = undefined;

  @JsonProperty('mainNote', 'Number')
  public mainNote: number = undefined;

  @JsonProperty('customsTariffNumber', 'String')
  public customsTariffNumber: string = undefined;

  public static create(classId: string, mainNote: number, customTariffNumber: string, articleNumber?: string): FinalizationPreselectionVO {
    const vo: FinalizationPreselectionVO = new FinalizationPreselectionVO();
    vo.classId = classId;
    vo.mainNote = mainNote;
    vo.customsTariffNumber = customTariffNumber;

    if (articleNumber) {
      vo.articleNumber = articleNumber;
    } else {
      vo.articleNumber = null;
    }

    return vo;
  }
}

export interface ApproveProjectFinalizationExportArgsVO {
  projectFinalizationInfo: ProjectFinalizationInfoVO,
  releaseDate: string, // ISO local date (without timezone)
}

export enum ApproveProjectFinalizationExportResultType {
  FAILURE = "FAILURE",
  SUCCESS = "SUCCESS",
  SUCCESS_WITH_OPTIONS = "SUCCESS_WITH_OPTIONS",
}

export interface ApproveProjectFinalizationExportResultVO {
  resultType: ApproveProjectFinalizationExportResultType;
  optionValues: ProjectOptionVO[];
}

export class ApproveProjectFinalizationExportUtils {
  public static isSuccess(result: ApproveProjectFinalizationExportResultVO): boolean {
    return result.resultType === ApproveProjectFinalizationExportResultType.SUCCESS
      || result.resultType === ApproveProjectFinalizationExportResultType.SUCCESS_WITH_OPTIONS;
  }
}
